import moment, {Moment} from 'moment';

import DateConstants from "#app/shared/lib/dates/DateConstants";

/** Дефолтная длительность события календаря (минуты), если timeDiff не передан. */
const DEFAULT_CALENDAR_EVENT_LENGTH: number = 30;

export const DEFAULT_BACKEND_FORMAT_MASK: string = 'YYYY-MM-DD HH:mm:ss';

/** Форматирует дату в маску бэкенда; невалидная дата → null. */
export const dateStringBackendMask = (
    date: moment.Moment | Moment
): string | null | undefined => date && date.isValid() ? date.format(DEFAULT_BACKEND_FORMAT_MASK) : null;

const _ERRORS_LIST = {
    error_start_more_than_end: `Ошибка при вычислении разницы в датах: дата начала не должна быть позднее даты окончания`,
    error_different_days: `Ошибка при вычислении разницы в датах: дата окончания и дата начала не могут быть назначены на разные дни, событие происходит в рамках дня`,
} as const;

export type GenericMomentInput<T = string | Date> = T;

/**
 * Разница дат в минутах в рамках одного дня.
 * Если конец не позже начала или дни разные — null (вызывающий ставит дефолт 30 мин).
 */
export const getTimeDifference = (dateStart: GenericMomentInput, dateEnd: GenericMomentInput): number | null => {
    const momentDateStart: moment.Moment = moment(dateStart);
    const momentDateEnd: moment.Moment = moment(dateEnd);

    if (!momentDateEnd.isAfter(momentDateStart)) return null;
    if (!momentDateStart.isSame(momentDateEnd, 'day')) return null;
    return momentDateEnd.diff(momentDateStart, 'minutes');
}

/**
 * Готовит start/end для нового события календаря.
 * Полночь → 08:00; без timeDiff — DEFAULT_CALENDAR_EVENT_LENGTH (или 08:00–08:30).
 */
export const prepareDate = (
    date: GenericMomentInput,
    timeDiff: number | null = null
): { start_at_datetime: string; end_at_datetime: string } => {
    let momentDate: moment.Moment = moment(date).startOf('minute');

    if (momentDate.hour() === 0 && momentDate.minute() === 0) {
        momentDate = momentDate.hour(8).minute(0);
    }

    const momentDateEnd: moment.Moment = (timeDiff !== null)
        ? moment(momentDate).add(timeDiff, 'minutes')
        : momentDate.clone().add(DEFAULT_CALENDAR_EVENT_LENGTH, 'minutes');

    if (momentDate.hour() === 8 && momentDate.minute() === 0 && timeDiff === null) {
        momentDateEnd.hour(8).minute(30);
    }

    return {
        start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
        end_at_datetime: momentDateEnd.format(DateConstants.dateTimeUserFormat),
    };
};

export const setDateInMultiMonth = (
    date: GenericMomentInput<Moment>,
    timeDiff: number | null = null
): { start_at_datetime: string; end_at_datetime: string } => {
    let momentDate = moment(date);
    if (momentDate.hour() === 0 && momentDate.minute() === 0) {
        momentDate = momentDate.hour(8).minute(0);
    }

    const originalStartDate = moment(momentDate);
    const durationInMinutes = timeDiff !== null ? timeDiff : DEFAULT_CALENDAR_EVENT_LENGTH;
    let momentDateEnd = moment(momentDate).add(durationInMinutes, 'minutes');

    if (originalStartDate.isBefore(momentDate)) {
        const timeShift = momentDate.diff(originalStartDate, 'minutes');
        momentDateEnd = momentDateEnd.add(timeShift, 'minutes');
    }

    return {
        start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
        end_at_datetime: momentDateEnd.format(DateConstants.dateTimeUserFormat),
    };
};

/**
 * Приводит дату к маске для календаря/API.
 * momentFormatMask — парсинг исходной строки (легаси, по умолчанию DD.MM.YYYY).
 */
export const formatDate = (
    dateToFormat: GenericMomentInput,
    formatString: string,
    momentFormatMask: string | null | undefined = null,
): string => moment(dateToFormat, momentFormatMask ?? 'DD.MM.YYYY').format(formatString);

/** Текущий слот «сейчас»: :00 или :30 → конец на ближайший следующий час. */
export const prepareDateNow = () => {
    const momentDate = moment();
    let momentDateAdded30Minutes: moment.Moment;

    if (momentDate.minutes() >= 30) {
        momentDate.minutes(30).seconds(0);
        momentDateAdded30Minutes = momentDate.clone().startOf('hour').add(1, 'hour');

        return {
            start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
            end_at_datetime: momentDateAdded30Minutes.format(DateConstants.dateTimeUserFormat),
        }
    } else {
        momentDate.startOf('hour').seconds(0);
        momentDateAdded30Minutes = momentDate.clone().startOf('hour').add(1, 'hour');

        return {
            start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
            end_at_datetime: momentDateAdded30Minutes.format(DateConstants.dateTimeUserFormat),
        }
    }
}

/**
 * Закрывающая дата календаря: если end = start+1 день 00:00 (тот же месяц),
 * сдвигает end на 23:59:59 того же дня — день включается целиком.
 */
export const fixCalendarEndDate = (start: moment.Moment, end: moment.Moment): [moment.Moment, moment.Moment] => {
    const newStart = start.clone();
    const newEnd = end.clone();

    if (
        newStart.isSame(newEnd, 'month') &&
        newStart.isSame(newEnd, 'year')
    ) {
        const nextDayStart = newStart.clone().add(1, 'day').startOf('day');
        if (newEnd.isSame(nextDayStart)) {
            newEnd.set({
                year: newStart.year(), month: newStart.month(), date: newStart.date(),
                hour: 23, minute: 59, second: 59, millisecond: 0
            });
        }
    }

    return [newStart, newEnd];
};
